import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useState, type CSSProperties, type MouseEvent, type FormEvent } from 'react'
import { Button, ListBox, ListBoxItem, Popover, Select, SelectValue } from 'react-aria-components'
import { Container } from '#app/components/layout/container.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.ts'
import { Code } from '#app/old_components/typography/code.tsx'
import { AccessibleIcon } from '#app/old_components/ui/accessible-icon.tsx'
import { CodeBlock } from '#app/old_components/ui/code-block.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Container',
}

const component: componentProps = {
	name: 'Container',
	shortName: 'Container',
	fileName: 'container',
	category: 'layout',
	description: ["Gives content a fluid padding. If viewport goes beyond the website's max width, the content stays centered."],
	features: ['Padding flows between min and max values.', "Inherits the website's max width and centers content beyond it."],
	parts: [
		{
			name: 'Container',
			description: "Gives content a fluid padding. If viewport goes beyond the website's max width, the content stays centered.",
		},
	],
	usage: `<Container>Some content</Container>`,
	styling: `/* Website width */
@layer base {
	:root {
		/* Editable website width values */
		--content-min-width: 20; /* 20rem = 320px */
		--content-max-width: 96; /* 96rem = 1536px */
	}
}
	
/* Container Space */
@layer components {
	:root {
		/* Editable container paddings */
		--container-min-padding: var(--scale-4); /* 6px */
		--container-max-padding: var(--scale-13); /* 32px */

		/* Calculations */
		--container-deltaY: calc(var(--container-max-padding) - var(--container-min-padding));
		--container-gradient: calc(var(--container-deltaY) / var(--size-deltaX));
		--container-intercept: calc(var(--container-min-padding) - (var(--container-gradient) * var(--content-min-width)));
		--container-padding-size: calc(var(--container-gradient) * 100vw + var(--container-intercept) * 1rem);

		/* Container fluid padding */
		--container-padding-x: clamp(calc(var(--container-min-padding) * 1rem), var(--container-padding-size), calc(var(--container-max-padding) * 1rem));

		/* Container max width */
		--container-max-width: calc(var(--content-max-width) * 1rem);
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()

	const { name, description, shortName, fileName, features, usage, parts, styling } = component

	const [showCopyIcon, setShowCopyIcon] = useState(true)
	const [, copy] = useCopyToClipboard()
	const code = `:root {
	--website-min-width: 320; /* Unitless pixels */
	--website-max-width: 1536; /* Unitless pixels */

	--container-min-padding: var(--space-4); /* 6px */
	--container-max-padding: var(--space-13); /* 32px */
}

@layer components {
	.container {
		@apply max-w-website-max min-w-website-min px-container-padding-x mx-auto w-full;
	}
}`

	const handleCopy = (e: MouseEvent<HTMLButtonElement>) => {
		copy(code)
		if (showCopyIcon) {
			setShowCopyIcon(false)
			setTimeout(() => setShowCopyIcon(true), 1000)
		}
	}

	const [websiteMinWidth, setWebsiteMinWidth] = useState(320)
	const [websiteMaxWidth, setWebsiteMaxWidth] = useState(1536)
	const [containerMinPadding, setContainerMinPadding] = useState('--space-4')
	const [containerMaxPadding, setContainerMaxPadding] = useState('--space-13')
	const handleWebsiteMinWidth = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setWebsiteMinWidth(Number(value))
		document.documentElement.style.setProperty('--website-min-width', value)
	}
	const handleWebsiteMaxWidth = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setWebsiteMaxWidth(Number(value))
		document.documentElement.style.setProperty('--website-max-width', value)
	}
	const handleContainerMinPadding = (selected: any) => {
		setContainerMinPadding(selected)
		document.documentElement.style.setProperty('--container-min-padding', `var(${selected})`)
	}
	const handleContainerMaxPadding = (selected: any) => {
		setContainerMaxPadding(selected)
		document.documentElement.style.setProperty('--container-max-padding', `var(${selected})`)
	}

	return (
		<>
			<Description name={name} description={description} />
			<div className="mb-10">
				<div className="notranslate group font-code text-size-xs overflow-hidden rounded-xl bg-[#182e3f] font-semibold leading-5 text-white">
					<div className="bg-foreground/20 h-13 flex items-center justify-between">
						<div className="flex items-center gap-4 pl-6">
							<div className="h-[12px] w-[12px] rounded-full bg-[#FE5650]"></div>
							<div className="h-[12px] w-[12px] rounded-full bg-[#FDB43C]"></div>
							<div className="h-[12px] w-[12px] rounded-full bg-[#29C045]"></div>
						</div>
						<div>styles.css</div>
						<button className="min-h-tap min-w-tap hover:bg-muted-100/10 h-11 rounded-lg py-2 px-7" onClick={handleCopy}>
							{showCopyIcon ?
								<AccessibleIcon name="copy" label="Copy to clipboard" />
							:	<AccessibleIcon name="check" label="Saved to clipboard" />}
						</button>
					</div>
					<div className="flex">
						<ol className="bg-foreground/10 text-muted-500 py-2 pr-4 pl-7 select-none">
							<li>1</li>
							<li>2</li>
							<li>3</li>
							<li>4</li>
							<li>5</li>
							<li>6</li>
							<li>7</li>
							<li>8</li>
							<li>9</li>
						</ol>
						<pre className="flex-1 overflow-x-auto py-2 pr-4 pl-2">
							<code>
								<div>
									<span className="text-green-400">:root </span>
									<span>{'{'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span className="text-blue-600">{'/* Website width */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--website-min-width: </span>
									<span className="text-[#FE5981]">
										<input type="number" value={websiteMinWidth} className="inline-block w-[100px] rounded-md pl-6" onChange={handleWebsiteMinWidth} />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--website-max-width: </span>
									<span className="text-[#FE5981]">
										<input type="number" value={websiteMaxWidth} className="inline-block w-[100px] rounded-md pl-6" onChange={handleWebsiteMaxWidth} />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>&nbsp;</div>
								<div>
									<span>&#9;</span>
									<span className="text-blue-600">{'/* Spacing */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--container-min-padding: </span>
									<span className="text-[#FB9128]">var</span>
									<span className="text-[#D466CC]">(</span>
									<span>
										<Select defaultSelectedKey={containerMinPadding} onSelectionChange={selected => handleContainerMinPadding(selected)} className="inline rounded-lg py-1 px-8">
											<Button>
												<SelectValue />
												<span aria-hidden="true" className="text-muted-500 ml-6 w-[280px]">
													▼
												</span>
											</Button>
											<Popover>
												<ListBox className="bg-background border-muted-100 rounded-lg border p-2 shadow-lg">
													<ListBoxItem id="--space-1" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-1
													</ListBoxItem>
													<ListBoxItem id="--space-2" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-2
													</ListBoxItem>
													<ListBoxItem id="--space-3" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-3
													</ListBoxItem>
													<ListBoxItem id="--space-4" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-4
													</ListBoxItem>
													<ListBoxItem id="--space-5" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-5
													</ListBoxItem>
													<ListBoxItem id="--space-6" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-6
													</ListBoxItem>
													<ListBoxItem id="--space-7" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-7
													</ListBoxItem>
													<ListBoxItem id="--space-8" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-8
													</ListBoxItem>
													<ListBoxItem id="--space-9" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-9
													</ListBoxItem>
													<ListBoxItem id="--space-10" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-10
													</ListBoxItem>
													<ListBoxItem id="--space-11" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-11
													</ListBoxItem>
													<ListBoxItem id="--space-12" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-12
													</ListBoxItem>
													<ListBoxItem id="--space-13" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-13
													</ListBoxItem>
													<ListBoxItem id="--space-14" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-14
													</ListBoxItem>
												</ListBox>
											</Popover>
										</Select>
									</span>
									<span className="text-[#D466CC]">)</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Use the space scale */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--container-max-padding: </span>
									<span className="text-[#FB9128]">var</span>
									<span className="text-[#D466CC]">(</span>
									<span>
										<Select defaultSelectedKey={containerMaxPadding} onSelectionChange={selected => handleContainerMaxPadding(selected)} className="inline rounded-lg py-1 px-8">
											<Button>
												<SelectValue />
												<span aria-hidden="true" className="text-muted-500 ml-6 w-[280px] ">
													▼
												</span>
											</Button>
											<Popover>
												<ListBox className="bg-background border-muted-100 rounded-lg border p-2 shadow-lg">
													<ListBoxItem id="--space-1" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-1
													</ListBoxItem>
													<ListBoxItem id="--space-2" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-2
													</ListBoxItem>
													<ListBoxItem id="--space-3" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-3
													</ListBoxItem>
													<ListBoxItem id="--space-4" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-4
													</ListBoxItem>
													<ListBoxItem id="--space-5" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-5
													</ListBoxItem>
													<ListBoxItem id="--space-6" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-6
													</ListBoxItem>
													<ListBoxItem id="--space-7" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-7
													</ListBoxItem>
													<ListBoxItem id="--space-8" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-8
													</ListBoxItem>
													<ListBoxItem id="--space-9" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-9
													</ListBoxItem>
													<ListBoxItem id="--space-10" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-10
													</ListBoxItem>
													<ListBoxItem id="--space-11" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-11
													</ListBoxItem>
													<ListBoxItem id="--space-12" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-12
													</ListBoxItem>
													<ListBoxItem id="--space-13" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-13
													</ListBoxItem>
													<ListBoxItem id="--space-14" className="data-[focused]:bg-muted-200 w-full rounded-lg py-2 px-8 outline-none">
														--space-14
													</ListBoxItem>
												</ListBox>
											</Popover>
										</Select>
									</span>
									<span className="text-[#D466CC]">)</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Use the space scale */'}</span>
								</div>
								<div>
									<span>{'}'}</span>
								</div>
							</code>
						</pre>
					</div>
				</div>
			</div>
			<div className="h-19 mx-[calc(50%-50vw)] mb-10 bg-[#F8C69A]">
				<Container
					className="h-full"
					style={
						{
							backgroundImage: 'linear-gradient(0deg, #99BEE3, #99BEE3), linear-gradient(0deg, #BDDAB3, #BDDAB3)',
							backgroundClip: 'content-box, padding-box',
						} as CSSProperties
					}
				>
					<Flex justify="center" items="center" className="h-full text-center">
						Resize the viewport to see the container's margins and paddings.
					</Flex>
				</Container>
			</div>

			<Features features={features} />
			<Parts parts={parts} />
			<H2>Usage</H2>
			<CodeBlock code={usage} filename={`${shortName}Example`} extension="tsx" className="mt-4" />
			<P>
				This page is wrapped in a <Code>{'<Container />'}</Code>. To see it in action, resize the window and see how the padding grows fluidly with viewport width. Also notice how the{' '}
				<Code>Container</Code> remains centered when the viewport goes beyond the website's max width of <Code>1536px</Code>.
			</P>
			<Source source={source} fileName={fileName} />
			<H2>Styling</H2>
			<P>
				The <Code>Container</Code> component inherits the <Code>--content-max-width</Code> variable and has its own fluid space rule.
			</P>
			<CodeBlock code={styling!} filename="design-tokens" extension="css" className="mt-4" />
			<P>
				From which the <Code>{'max-w-container'}</Code> and <Code>{'*-container'}</Code> space utilities are created.
			</P>
			<CodeBlock
				code={`export const extendedTheme = {
	/* Website width */
	maxWidth: {
		container: 'var(--container-max-width)',
	},
	/* Space scale */
	spacing: {
		container: 'var(--container-padding-x)',
	},
}`}
				filename="extended-theme"
				extension="ts"
				className="mt-4"
			/>
			<P>
				Even thought the <Code>{'*-container'}</Code> space utility works with all the properties that accept a size value, such as <Code>padding</Code>, <Code>margin</Code>,{' '}
				<Code>width</Code>, <Code>minWidth</Code>, <Code>maxWidth</Code>, <Code>height</Code>, <Code>minHeight</Code>, <Code>maxHeight</Code>, <Code>gap</Code>, <Code>inset</Code>,{' '}
				<Code>space</Code>, <Code>translate</Code>, <Code>scrollMargin</Code>, and <Code>scrollPadding</Code>, it should probably only be used for the <Code>Container</Code> component.
			</P>
			<CodeBlock
				code={`/* ### Layout ### */
@layer components {
	/* Container. The container's class is called 'container-class' because TailwindCSS already has a 'container' class. */
	.container-class {
		@apply max-w-container mx-auto w-full px-container;
	}
}`}
				filename="component-styles"
				extension="css"
				className="mt-4"
			/>
			<P>
				Unfortunately, TailwindCSS already has a <Code>container</Code> class, so we had to name ours <Code>container-class</Code>.
			</P>
		</>
	)
}
export default HeadingRoute
